import Decimal from 'decimal.js'
import { QuoteType } from '@/lib/types'
import { calculateMilestoneAmounts } from '@/lib/utils'
import {
  isPaymentMilestoneEditable,
  shouldUseDefaultPaymentMilestones,
} from '@/lib/payment-milestone-editability'

type MilestoneWithAmount = {
  dsy_paymentmilestoneid?: string
  dsy_name: string
  dsy_percent: number
  dsy_amount?: number
  dsy_sequencenumber: number
  dsy_percent_was_null?: boolean
  dsy_paylink?: unknown
}

export function getPaymentMilestoneFormMilestones({
  milestones,
  defaultMilestones,
  quoteType,
  summaryTotal,
}: {
  milestones: MilestoneWithAmount[] | undefined
  defaultMilestones: MilestoneWithAmount[]
  quoteType?: QuoteType | number
  summaryTotal: number
}) {
  if (!milestones) return null
  const editableMilestones = getEditablePaymentMilestones(milestones, defaultMilestones)
  if (editableMilestones.length > 0) return calculateMilestoneAmounts(editableMilestones, summaryTotal)
  if (!shouldUseDefaultPaymentMilestones({ milestones, quoteType, summaryTotal })) return []
  return calculateMilestoneAmounts(defaultMilestones, summaryTotal)
}

export function getEditablePaymentMilestones<T extends MilestoneWithAmount>(
  milestones: T[],
  defaultMilestones: MilestoneWithAmount[]
) {
  const defaultKeys = new Set(defaultMilestones.map(getPaymentMilestoneDuplicateKey))
  const editableMilestones = new Map<string, T>()

  milestones.forEach((milestone) => {
    if (!isPaymentMilestoneEditable(milestone)) return

    const defaultKey = getPaymentMilestoneDuplicateKey(milestone)
    const key = defaultKeys.has(defaultKey) ? defaultKey : `${defaultKey}|${milestone.dsy_sequencenumber}`
    const existing = editableMilestones.get(key)

    if (!existing || (!existing.dsy_paylink && milestone.dsy_paylink)) {
      editableMilestones.set(key, milestone)
    }
  })

  return Array.from(editableMilestones.values())
}

export function getPaymentMilestoneDuplicateKey(milestone: Pick<MilestoneWithAmount, 'dsy_name' | 'dsy_percent'>) {
  return `${milestone.dsy_name.trim().replace(/\s+/g, ' ').toLowerCase()}|${new Decimal(milestone.dsy_percent).toString()}`
}
